import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { AppState } from 'state';
import { logOutTwitter } from 'state/user/slice';

// create a new mutex
const mutex = new Mutex();
const baseQuery = fetchBaseQuery({
  baseUrl: 'https://api.twitter.com/2',
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = (getState() as AppState).user.twitterToken;

    // If we have a token set in state, let's assume that we should be passing it.
    if (token && endpoint !== 'getTwitterToken') {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
});
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await baseQuery('/oauth2/token', api, extraOptions);
        if (refreshResult.data) {
          console.log(refreshResult.data);
          //   api.dispatch(updateTwitterToken(refreshResult.data));
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logOutTwitter());
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export const twitterApi = createApi({
  reducerPath: 'twitterApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    getTwitterToken: build.mutation<{ token: string }, { authCode: string }>({
      query: ({ authCode }) => {
        return {
          url: '/oauth2/token',
          method: 'POST',
          body: {
            code: authCode,
            grant_type: 'authorization_code',
            client_id: process.env.REACT_APP_TWITTER_CLIENT_ID,
            redirect_uri: `${window.location.protocol}//${window.location.hostname}${
              window.location.port ? `:${window.location.port}` : ''
            }/status/`,
            code_verifier: 'challenge',
          },
        };
      },
    }),
    getTwitterUserInfos: build.mutation<void, void>({
      query: () => ({
        url: '/users/me',
        method: 'GET',
      }),
    }),
  }),
});

export const { useGetTwitterTokenMutation } = twitterApi;
